import React from 'react';
import PageBanner from '../components/PageBanner';
import { Anchor, DollarSign, ShieldCheck, Award, MapPin, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';

const AboutPage = () => {
  const settings = useSettings();
  return (
    <>
      <PageBanner
        title="About Get Nile Cruise"
        subtitle="Egypt's most trusted Nile river cruise specialists — 25 years crafting unforgettable journeys."
        image="https://images.unsplash.com/photo-1774284498049-eee34f796ce7?crop=entropy&cs=srgb&fm=jpg&q=85"
        crumbs={[{ label: 'About Us' }]}
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[#6a8aa4] uppercase tracking-[0.25em] text-xs mb-4"><span className="w-8 h-[1px] bg-[#6a8aa4]" /> Our Story</div>
            <h2 className="font-serif text-[#304b71] text-4xl md:text-5xl leading-tight">Egypt insiders. Nile experts. Guest-first always.</h2>
            <p className="mt-6 text-slate-600 leading-relaxed">{settings.brand} was founded to make Egypt's most iconic experience — a Nile river cruise — accessible, honest and effortlessly bookable. As official cruise-supplier partners with Egypt's leading fleets, we pass on real supplier rates directly to our guests, backed by 25 years of on-the-ground expertise from our home base in Aswan.</p>
            <p className="mt-4 text-slate-600 leading-relaxed">From your first inquiry to disembarkation, our specialists handle every detail: hotel bookings, domestic flights, Egyptologist-led tours and personalized itinerary tweaks. We only work with ships and guides we would send our own families on.</p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[{ n: '25+', l: 'Years experience' }, { n: '15k+', l: 'Happy travellers' }, { n: '200+', l: 'Nile cruise ships' }].map((s) => (
                <div key={s.l} className="border-l-2 border-[#a6b2b5] pl-4"><div className="font-serif text-2xl text-[#304b71]">{s.n}</div><div className="text-xs text-slate-500 uppercase tracking-wider">{s.l}</div></div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1568503446072-5c1c17d9b018?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-72 object-cover rounded-2xl shadow-lg translate-y-8" />
            <img src="https://images.unsplash.com/photo-1719659018185-8a239c35fb4a?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-72 object-cover rounded-2xl shadow-lg" />
            <img src="https://images.unsplash.com/photo-1600520611035-84157ad4084d?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-52 object-cover rounded-2xl shadow-lg" />
            <img src="https://images.unsplash.com/photo-1665643956022-ee053e925743?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-52 object-cover rounded-2xl shadow-lg translate-y-4" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f6f8fa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="font-serif text-[#304b71] text-3xl md:text-5xl leading-tight">What sets us apart</h2>
            <p className="mt-4 text-slate-600">Four pillars we live by on every journey.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Anchor, title: 'Local ownership', desc: 'Founded and led by Egyptians who live and work on the Nile every day.' },
              { icon: DollarSign, title: 'Best-price guarantee', desc: 'Direct supplier partnerships mean the lowest official rates. No hidden fees.' },
              { icon: Award, title: 'Award-winning service', desc: 'TripAdvisor Travellers\' Choice, 4.9-star Google rating and repeat guests worldwide.' },
              { icon: ShieldCheck, title: 'Financially secured', desc: 'Payments protected with international-standard secure gateways.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-lg transition">
                <div className="w-12 h-12 rounded-full bg-[#304b71] text-white flex items-center justify-center mb-4"><Icon size={20} /></div>
                <h3 className="font-serif text-lg text-[#304b71]">{title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mt-2">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#304b71] text-white">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="font-serif text-3xl md:text-5xl leading-tight">Ready to sail the Nile?</h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">Our specialists will design your ideal Egypt itinerary — for free — within 24 hours.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-[#304b71] hover:bg-[#a6b2b5] hover:text-white rounded-full px-8 h-14"><Link to="/request-quote">Request a Free Quote</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#304b71] rounded-full px-8 h-14"><Link to="/cruises">Browse All Cruises</Link></Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
